import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Label,
  Modal,
  Popup,
  Table,
} from 'semantic-ui-react';
import { API, copy, showError, showSuccess, timestamp2string } from '../helpers';

const defaultForm = {
  id: 0,
  name: '',
  app_url: '',
  redirect_uris: '',
  scopes: 'api:chat api:data',
  status: 1,
};

const OAuthAppsTable = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [secretInfo, setSecretInfo] = useState({
    open: false,
    clientId: '',
    clientSecret: '',
    createdBy: '',
  });
  const [confirmInfo, setConfirmInfo] = useState({
    open: false,
    title: '',
    content: '',
    danger: false,
    onConfirm: null,
  });

  const loadApps = async () => {
    const res = await API.get('/api/oauth_app/');
    const { success, message, data } = res.data;
    if (success) {
      setApps(data || []);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadApps().catch((reason) => showError(reason));
  }, []);

  const filteredApps = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return apps;
    return apps.filter((app) =>
      [
        app.name,
        app.client_id,
        app.app_url,
        (app.redirect_uris || []).join(' '),
      ]
        .join(' ')
        .toLowerCase()
        .includes(kw)
    );
  }, [apps, keyword]);

  const openCreate = () => {
    setForm(defaultForm);
    setShowForm(true);
  };

  const openEdit = (app) => {
    setForm({
      id: app.id,
      name: app.name,
      app_url: app.app_url,
      redirect_uris: (app.redirect_uris || []).join('\n'),
      scopes: app.scopes,
      status: app.status,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSaving(false);
  };

  const submitForm = async () => {
    if (!form.name.trim()) {
      showError('应用名称不能为空');
      return;
    }
    const redirectUris = form.redirect_uris
      .split('\n')
      .map((item) => item.trim())
      .filter((item) => item !== '');
    if (redirectUris.length === 0) {
      showError('回调地址白名单不能为空');
      return;
    }
    setSaving(true);
    const payload = {
      id: form.id,
      name: form.name.trim(),
      app_url: form.app_url.trim(),
      redirect_uris: redirectUris,
      scopes: form.scopes.trim(),
      status: Number(form.status) === 2 ? 2 : 1,
    };
    const res =
      form.id === 0
        ? await API.post('/api/oauth_app/', payload)
        : await API.put('/api/oauth_app/', payload);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(message || '操作成功');
      closeForm();
      await loadApps();
      if (data && data.client_secret) {
        setSecretInfo({
          open: true,
          clientId: data.client_id,
          clientSecret: data.client_secret,
          createdBy: 'create',
        });
      }
    } else {
      showError(message);
      setSaving(false);
    }
  };

  const askConfirm = (title, content, onConfirm, danger = true) => {
    setConfirmInfo({ open: true, title, content, danger, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmInfo((prev) => ({ ...prev, open: false, onConfirm: null }));
  };

  const runConfirm = async () => {
    const handler = confirmInfo.onConfirm;
    setConfirmInfo((prev) => ({ ...prev, open: false, onConfirm: null }));
    if (handler) {
      await handler();
    }
  };

  const toggleStatus = (app) => {
    const nextStatus = app.status === 1 ? 2 : 1;
    const enable = nextStatus === 1;
    askConfirm(
      enable ? '启用应用' : '停用应用',
      enable
        ? `确定启用「${app.name}」吗？`
        : `确定停用「${app.name}」吗？停用后该应用已获得的授权令牌将全部失效，应用方需重新授权。`,
      async () => {
        const res = await API.put('/api/oauth_app/', {
          id: app.id,
          name: app.name,
          app_url: app.app_url,
          redirect_uris: app.redirect_uris,
          scopes: app.scopes,
          status: nextStatus,
        });
        const { success, message } = res.data;
        if (success) {
          showSuccess(message || '操作成功');
          await loadApps();
        } else {
          showError(message);
        }
      },
      !enable
    );
  };

  const revokeTokens = (app) => {
    askConfirm(
      '撤销全部授权',
      `确定撤销「${app.name}」当前全部授权令牌吗？应用方需重新发起授权。`,
      async () => {
        const res = await API.post(`/api/oauth_app/${app.id}/revoke_tokens`);
        const { success, message } = res.data;
        if (success) {
          showSuccess(message || '操作成功');
          await loadApps();
        } else {
          showError(message);
        }
      }
    );
  };

  const resetSecret = (app) => {
    askConfirm(
      '重置 client_secret',
      `确定重置「${app.name}」的 client_secret 吗？重置后旧密钥立即失效，且该应用已获得的授权令牌会被一并撤销（新密钥仅显示一次）。`,
      async () => {
        const res = await API.post(`/api/oauth_app/${app.id}/reset_secret`);
        const { success, message, data } = res.data;
        if (success) {
          showSuccess(message || '操作成功');
          await loadApps();
          if (data && data.client_secret) {
            setSecretInfo({
              open: true,
              clientId: data.client_id,
              clientSecret: data.client_secret,
              createdBy: 'reset',
            });
          }
        } else {
          showError(message);
        }
      }
    );
  };

  const deleteApp = (app) => {
    askConfirm(
      '删除应用',
      `确定删除「${app.name}」（${app.client_id}）吗？其授权令牌与授权码会被一并清理，且不可恢复。`,
      async () => {
        const res = await API.delete(`/api/oauth_app/${app.id}`);
        const { success, message } = res.data;
        if (success) {
          showSuccess(message || '删除成功');
          await loadApps();
        } else {
          showError(message);
        }
      }
    );
  };

  const copySecret = async () => {
    const okay = await copy(
      `client_id: ${secretInfo.clientId}\nclient_secret: ${secretInfo.clientSecret}`
    );
    if (okay) {
      showSuccess('已复制到剪贴板');
    } else {
      showError('复制失败，请手动选中复制');
    }
  };

  const secretLabel = (app) => {
    if (app.secret_pending) {
      return (
        <Popup
          trigger={<Label basic color='yellow'>待领取</Label>}
          content='授权页自助开通后密钥尚未被应用方取走（首次换取令牌时下发）'
        />
      );
    }
    return (
      <Popup
        trigger={<Label basic color='green'>可回显</Label>}
        content='密钥明文入库，随每次授权回调返回；应用方丢失时重新授权即可取回，也可在后台重置'
      />
    );
  };

  return (
    <>
      <Form>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder='搜索名称 / client_id / 应用地址 / 回调地址...'
          value={keyword}
          onChange={(e, { value }) => setKeyword(value)}
        />
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>应用</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>client_id</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>回调白名单</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>状态</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>有效授权</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>密钥</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>创建时间</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {filteredApps.map((app) => (
            <Table.Row key={app.id}>
              <Table.Cell>{app.id}</Table.Cell>
              <Table.Cell>
                <div style={{ fontWeight: 500 }}>{app.name}</div>
                {app.app_url ? (
                  <div style={{ color: '#8a919f', fontSize: '12px' }}>
                    {app.app_url}
                  </div>
                ) : null}
                {app.username ? (
                  <div style={{ color: '#8a919f', fontSize: '12px' }}>
                    归属：{app.username}
                  </div>
                ) : null}
              </Table.Cell>
              <Table.Cell>
                <code style={{ fontSize: '12px' }}>{app.client_id}</code>
                <Button
                  size='mini'
                  basic
                  style={{ marginLeft: '6px' }}
                  onClick={async () => {
                    const okay = await copy(app.client_id);
                    if (okay) {
                      showSuccess('已复制 client_id');
                    } else {
                      showError('复制失败，请手动选中复制');
                    }
                  }}
                >
                  复制
                </Button>
              </Table.Cell>
              <Table.Cell>
                <Popup
                  wide
                  trigger={
                    <Label basic>{(app.redirect_uris || []).length} 条</Label>
                  }
                  content={
                    <div style={{ lineHeight: 1.8 }}>
                      {(app.redirect_uris || []).map((uri) => (
                        <div key={uri}>{uri}</div>
                      ))}
                    </div>
                  }
                />
              </Table.Cell>
              <Table.Cell>
                {app.status === 1 ? (
                  <Label basic color='green'>
                    启用
                  </Label>
                ) : (
                  <Label basic color='red'>
                    停用
                  </Label>
                )}
              </Table.Cell>
              <Table.Cell>{app.active_tokens}</Table.Cell>
              <Table.Cell>{secretLabel(app)}</Table.Cell>
              <Table.Cell>
                {app.created_at > 0 ? timestamp2string(app.created_at) : '-'}
              </Table.Cell>
              <Table.Cell>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  <Button size={'tiny'} onClick={() => openEdit(app)}>
                    编辑
                  </Button>
                  <Button size={'tiny'} onClick={() => toggleStatus(app)}>
                    {app.status === 1 ? '停用' : '启用'}
                  </Button>
                  <Button size={'tiny'} onClick={() => resetSecret(app)}>
                    重置密钥
                  </Button>
                  <Button
                    size={'tiny'}
                    disabled={app.active_tokens === 0}
                    onClick={() => revokeTokens(app)}
                  >
                    撤销授权
                  </Button>
                  <Button size={'tiny'} negative onClick={() => deleteApp(app)}>
                    删除
                  </Button>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='9'>
              <Button size='small' loading={loading} onClick={openCreate}>
                新建应用
              </Button>
              <span style={{ marginLeft: '12px', color: '#8a919f' }}>
                共 {filteredApps.length} 个应用；「停用 / 删除 / 重置密钥」都会同时作废该应用已获得的授权令牌
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <Modal open={showForm} onClose={closeForm} size='small'>
        <Modal.Header>{form.id === 0 ? '新建 OAuth 应用' : '编辑 OAuth 应用'}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label='应用名称'
              placeholder='如 hr-server'
              value={form.name}
              onChange={(e, { value }) =>
                setForm((prev) => ({ ...prev, name: value }))
              }
            />
            <Form.Input
              label='应用地址（选填，用于开通接口幂等识别）'
              placeholder='https://example.com'
              value={form.app_url}
              onChange={(e, { value }) =>
                setForm((prev) => ({ ...prev, app_url: value }))
              }
            />
            <Form.TextArea
              label='回调地址白名单（每行一个，目录前缀匹配：配到域名即放行该域名下任意路径，本机联调可用 localhost）'
              rows={4}
              placeholder={'https://example.com/oauth/callback\nhttps://dsoanew.example.cn'}
              value={form.redirect_uris}
              onChange={(e, { value }) =>
                setForm((prev) => ({ ...prev, redirect_uris: value }))
              }
            />
            <Form.Input
              label='权限范围 scopes（空格分隔，默认 api:chat api:data）'
              value={form.scopes}
              onChange={(e, { value }) =>
                setForm((prev) => ({ ...prev, scopes: value }))
              }
            />
            {form.id !== 0 && (
              <Form.Select
                label='状态'
                options={[
                  { key: 1, text: '启用', value: 1 },
                  { key: 2, text: '停用', value: 2 },
                ]}
                value={form.status}
                onChange={(e, { value }) =>
                  setForm((prev) => ({ ...prev, status: value }))
                }
              />
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeForm}>取消</Button>
          <Button primary loading={saving} onClick={submitForm}>
            保存
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={secretInfo.open}
        onClose={() => setSecretInfo((prev) => ({ ...prev, open: false }))}
        size='small'
      >
        <Modal.Header>
          {secretInfo.createdBy === 'reset' ? '新密钥已生成' : '应用创建成功'}
          （client_secret 仅显示这一次）
        </Modal.Header>
        <Modal.Content>
          <p style={{ color: '#f53f3f' }}>
            请立即复制并交给应用方保存，关闭本窗口后网关不再提供明文密钥；如丢失只能在后台重置。
          </p>
          <Form>
            <Form.Input
              label='client_id'
              readOnly
              value={secretInfo.clientId}
            />
            <Form.Input
              label='client_secret'
              readOnly
              value={secretInfo.clientSecret}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={copySecret}>复制 client_id 与 client_secret</Button>
          <Button
            primary
            onClick={() => setSecretInfo((prev) => ({ ...prev, open: false }))}
          >
            我已保存
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={confirmInfo.open}
        onClose={closeConfirm}
        size='tiny'
        style={{ maxWidth: '480px' }}
      >
        <Modal.Header>{confirmInfo.title}</Modal.Header>
        <Modal.Content>
          <p>{confirmInfo.content}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeConfirm}>取消</Button>
          <Button color={confirmInfo.danger ? 'red' : 'blue'} onClick={runConfirm}>
            确定
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default OAuthAppsTable;
